// ============================================================
// Leave Types Page — Staff Management
// ============================================================

import { useState } from 'react';
import { Box, Typography, Button, Stack, IconButton, TextField, InputAdornment, Tooltip, Chip, Card, CardContent, Paper } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Plus, Search, Edit, Trash2, CalendarDays, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { useLeaveTypes, useCreateLeaveType, useUpdateLeaveType, useDeleteLeaveType } from '@/hooks/useStaffManagement';
import { PageHeader } from '@/components/navigation/PageHeader';
import { ConfirmationDialog } from '@/components/dialogs/ConfirmationDialog';
import { LeaveTypeModal } from './components/LeaveTypeModal';
import { InitializeLeaveModal } from './components/InitializeLeaveModal';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@/constants/permissions';
import toast from 'react-hot-toast';

export function LeaveTypesPage() {
  usePageMeta('Leave Types | Staff Management', [
    { label: 'Staff Management', path: '/staff-management' },
    { label: 'Leave Types' },
  ]);

  const { data: leaveTypes = [], isLoading, error, refetch } = useLeaveTypes();
  const createMutation = useCreateLeaveType();
  const updateMutation = useUpdateLeaveType();
  const deleteMutation = useDeleteLeaveType();

  const { can } = usePermission();
  const canCreate = can(PERMISSIONS.STAFF_MANAGE_CREATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canUpdate = can(PERMISSIONS.STAFF_MANAGE_UPDATE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);
  const canDelete = can(PERMISSIONS.STAFF_MANAGE_DELETE) || can(PERMISSIONS.STAFF_MANAGE_VIEW);

  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [initModalOpen, setInitModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteType, setDeleteType] = useState(null);

  const filteredTypes = (leaveTypes || []).filter((type) => {
    if (!search.trim()) return true;
    const query = search.toLowerCase();
    return (
      type.name?.toLowerCase().includes(query) ||
      type.code?.toLowerCase().includes(query) ||
      type.description?.toLowerCase().includes(query)
    );
  });

  const handleOpenCreate = () => {
    setEditingType(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (type) => {
    setEditingType(type);
    setModalOpen(true);
  };

  const handleSubmitModal = async (payload) => {
    try {
      if (editingType) {
        await updateMutation.mutateAsync({ id: editingType.id, ...payload });
        toast.success('Leave type updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Leave type created successfully');
      }
      setModalOpen(false);
      setEditingType(null);
    } catch (err) {
      toast.error(err.message || 'Failed to save leave type');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteType) return;
    try {
      await deleteMutation.mutateAsync(deleteType.id);
      toast.success('Leave type deleted successfully');
      setDeleteType(null);
    } catch (err) {
      toast.error(err.message || 'Failed to delete leave type');
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Leave Name',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="600">
            {params.row.name}
          </Typography>
          {params.row.description && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {params.row.description}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 0.8,
      minWidth: 90,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip label={params.value} size="small" variant="outlined" sx={{ fontWeight: 600 }} />
        </Box>
      ),
    },
    {
      field: 'days_per_year',
      headerName: 'Days/Year',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" fontWeight="500">
            {params.value} days
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_paid',
      headerName: 'Type',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Paid' : 'Unpaid'}
            size="small"
            color={params.value ? 'success' : 'default'}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      ),
    },
    {
      field: 'allow_half_day',
      headerName: 'Half Day',
      flex: 0.8,
      minWidth: 90,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? <CheckCircle2 size={18} color="green" /> : <XCircle size={18} color="gray" />}
        </Box>
      ),
    },
    {
      field: 'requires_approval',
      headerName: 'Approval',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value ? 'Required' : 'Auto'}
            size="small"
            variant="outlined"
            color={params.value ? 'primary' : 'default'}
            sx={{ fontWeight: 600, fontSize: '0.725rem' }}
          />
        </Box>
      ),
    },
    {
      field: 'carry_forward',
      headerName: 'Carry Forward',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {params.value ? (
            <Typography variant="caption" color="success.main" fontWeight="600">
              Yes (Max {params.row.max_carry_forward_days || 0}d)
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 100,
      renderCell: (params) => {
        const isActive = params.value === 'ACTIVE' || params.value === true;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              label={isActive ? 'ACTIVE' : 'INACTIVE'}
              size="small"
              color={isActive ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', height: '100%' }}>
          {canUpdate && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => handleOpenEdit(params.row)} color="primary">
                <Edit size={16} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => setDeleteType(params.row)} color="error">
                <Trash2 size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ];

  if (error) {
    return <ErrorState message={error.message || 'Failed to load leave types'} onRetry={refetch} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Leave Types"
        subtitle="Manage employee leave rules, quotas, and policies."
        action={
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Sparkles size={18} />}
              onClick={() => setInitModalOpen(true)}
            >
              Initialize Leave Balances
            </Button>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={handleOpenCreate}
              >
                Add Leave Type
              </Button>
            )}
          </Stack>
        }
      />

      <Card>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <TextField
            placeholder="Search leave types by name, code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
        </CardContent>
      </Card>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <DataGrid
          rows={filteredTypes}
          columns={columns}
          loading={isLoading}
          autoHeight
          getRowId={(row) => row.id || Math.random().toString()}
          pageSizeOptions={[10, 20, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          disableRowSelectionOnClick
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Modal for Create / Edit */}
      <LeaveTypeModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingType(null);
        }}
        leaveType={editingType}
        onSubmit={handleSubmitModal}
        loading={createMutation.isPending || updateMutation.isPending}
      />

      {/* Modal for Initializing Leave Balances */}
      <InitializeLeaveModal
        open={initModalOpen}
        onClose={() => setInitModalOpen(false)}
        onSuccess={() => refetch()}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={Boolean(deleteType)}
        title="Delete Leave Type"
        message={`Are you sure you want to delete "${deleteType?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteType(null)}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
